import { useState } from 'react';
import { Group, Text, ActionIcon, Menu, Box, Button, TextInput, Select, NumberInput, Checkbox, Stack, Tooltip } from '@mantine/core';
import { MoreVertical, Edit3, Archive, RotateCcw, Plus } from 'react-feather';
import type { SubGroupWithTemplates, TaskTemplateWithDetails, JobTitle, TaskAnchorType } from '../../../../types';
import { TemplateRow } from './TemplateRow';
import { TASK_ANCHOR_OPTIONS } from '../../../../config/taskConfig';

interface SubGroupSectionProps {
  subGroup: SubGroupWithTemplates;
  phaseId: string;
  jobTitles?: JobTitle[];
  onEditSubGroup?: (subGroup: SubGroupWithTemplates) => void;
  onArchiveSubGroup?: (subGroup: SubGroupWithTemplates) => void;
  onRestoreSubGroup?: (subGroup: SubGroupWithTemplates) => void;
  onAddTemplate: (subGroupId: string | null) => void;
  onCreateTaskInline?: (data: {
    phaseId: string;
    subGroupId: string | null;
    taskTitle: string;
    jobTitleId: string | null;
    anchorType: TaskAnchorType;
    daysOffset: number;
    isSessionSpecific: boolean;
  }) => void;
  onEditTemplate: (template: TaskTemplateWithDetails) => void;
  onDuplicateTemplate: (template: TaskTemplateWithDetails) => void;
  onArchiveTemplate: (template: TaskTemplateWithDetails) => void;
  onRestoreTemplate: (template: TaskTemplateWithDetails) => void;
  onDeleteTemplate: (template: TaskTemplateWithDetails) => void;
}

export function SubGroupSection({
  subGroup,
  phaseId,
  jobTitles = [],
  onEditSubGroup,
  onArchiveSubGroup,
  onRestoreSubGroup,
  onAddTemplate,
  onCreateTaskInline,
  onEditTemplate,
  onDuplicateTemplate,
  onArchiveTemplate,
  onRestoreTemplate,
  onDeleteTemplate,
}: SubGroupSectionProps) {
  // State for inline add task form
  const [showAddTaskForm, setShowAddTaskForm] = useState(false);
  const [newTaskName, setNewTaskName] = useState('');
  const [newTaskAssignee, setNewTaskAssignee] = useState<string | null>(null);
  const [newTaskAnchor, setNewTaskAnchor] = useState<TaskAnchorType>('cohort_start');
  const [newTaskDaysOffset, setNewTaskDaysOffset] = useState<number>(0);
  const [newTaskIsPerSession, setNewTaskIsPerSession] = useState(false);

  const handleAddTaskInline = () => {
    if (newTaskName.trim() && onCreateTaskInline) {
      // Virtual "General" groups have IDs like "{phase_id}-general" or just "general"
      const isVirtual = subGroup.id === 'general' || subGroup.id.endsWith('-general');
      onCreateTaskInline({
        phaseId,
        subGroupId: isVirtual ? null : subGroup.id,
        taskTitle: newTaskName.trim(),
        jobTitleId: newTaskAssignee,
        anchorType: newTaskAnchor,
        daysOffset: newTaskDaysOffset,
        isSessionSpecific: newTaskIsPerSession,
      });
      resetAddTaskForm();
    }
  };

  const resetAddTaskForm = () => {
    setShowAddTaskForm(false);
    setNewTaskName('');
    setNewTaskAssignee(null);
    setNewTaskAnchor('cohort_start');
    setNewTaskDaysOffset(0);
    setNewTaskIsPerSession(false);
  };

  // Group templates: parents first, then get their subtasks
  const parentTemplates = subGroup.templates.filter(t => !t.parent_template_id);
  const subtasksByParent = subGroup.templates
    .filter(t => t.parent_template_id)
    .reduce((acc, subtask) => {
      const parentId = subtask.parent_template_id!;
      if (!acc[parentId]) acc[parentId] = [];
      acc[parentId].push(subtask);
      return acc;
    }, {} as Record<string, TaskTemplateWithDetails[]>);

  // Virtual "General" groups have IDs like "{phase_id}-general" or just "general"
  const isVirtualGroup = subGroup.id === 'general' || subGroup.id.endsWith('-general');

  return (
    <Box mb="md">
      {/* Sub-group header */}
      <Group justify="space-between" mb="xs" px="sm">
        <Group gap="xs">
          <Text
            size="xs"
            fw={600}
            c="dimmed"
            style={{
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              opacity: subGroup.is_archived ? 0.6 : 1,
            }}
          >
            {subGroup.name}
            {subGroup.is_archived && ' (Archived)'}
          </Text>

          {/* Pencil edit icon next to sub-group title */}
          {!isVirtualGroup && onEditSubGroup && (
            <Tooltip label="Edit sub-group">
              <ActionIcon
                variant="subtle"
                size="xs"
                onClick={() => onEditSubGroup(subGroup)}
              >
                <Edit3 size={12} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>

        {!isVirtualGroup && (
          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <ActionIcon variant="subtle" size="xs">
                <MoreVertical size={12} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              {onEditSubGroup && (
                <Menu.Item
                  leftSection={<Edit3 size={14} />}
                  onClick={() => onEditSubGroup(subGroup)}
                >
                  Edit Sub-Group
                </Menu.Item>
              )}
              {subGroup.is_archived ? (
                onRestoreSubGroup && (
                  <Menu.Item
                    leftSection={<RotateCcw size={14} />}
                    onClick={() => onRestoreSubGroup(subGroup)}
                  >
                    Restore Sub-Group
                  </Menu.Item>
                )
              ) : (
                onArchiveSubGroup && (
                  <Menu.Item
                    leftSection={<Archive size={14} />}
                    onClick={() => onArchiveSubGroup(subGroup)}
                  >
                    Archive Sub-Group
                  </Menu.Item>
                )
              )}
            </Menu.Dropdown>
          </Menu>
        )}
      </Group>

      {/* Templates list */}
      <Box
        style={{
          backgroundColor: 'var(--mantine-color-gray-0)',
          borderRadius: 'var(--mantine-radius-sm)',
        }}
      >
        {parentTemplates.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="md">
            No tasks in this group
          </Text>
        ) : (
          parentTemplates.map(template => (
            <TemplateRow
              key={template.id}
              template={template}
              subtasks={subtasksByParent[template.id] || []}
              onEdit={onEditTemplate}
              onDuplicate={onDuplicateTemplate}
              onArchive={onArchiveTemplate}
              onRestore={onRestoreTemplate}
              onDelete={onDeleteTemplate}
            />
          ))
        )}
      </Box>

      {/* Add task button / inline form */}
      {showAddTaskForm ? (
        <Box mt="xs" p="sm" style={{ backgroundColor: 'var(--mantine-color-gray-0)', borderRadius: 'var(--mantine-radius-sm)' }}>
          <Stack gap="sm">
            <TextInput
              placeholder="Task name..."
              value={newTaskName}
              onChange={(e) => setNewTaskName(e.target.value)}
              autoFocus
            />
            <Group gap="sm" grow>
              <Select
                placeholder="Assignee..."
                data={jobTitles.map(jt => ({ value: jt.id, label: jt.name }))}
                value={newTaskAssignee}
                onChange={setNewTaskAssignee}
                clearable
                searchable
              />
              <Select
                placeholder="Anchor..."
                data={TASK_ANCHOR_OPTIONS}
                value={newTaskAnchor}
                onChange={(v) => setNewTaskAnchor((v as TaskAnchorType) || 'cohort_start')}
              />
              <NumberInput
                placeholder="± days"
                value={newTaskDaysOffset}
                onChange={(v) => setNewTaskDaysOffset(typeof v === 'number' ? v : 0)}
                w={100}
              />
            </Group>
            <Checkbox
              label="Per-session task"
              checked={newTaskIsPerSession}
              onChange={(e) => setNewTaskIsPerSession(e.currentTarget.checked)}
            />
            <Group justify="flex-end" gap="xs">
              <Button variant="default" size="xs" onClick={resetAddTaskForm}>
                Cancel
              </Button>
              <Button
                size="xs"
                onClick={handleAddTaskInline}
                disabled={!newTaskName.trim()}
              >
                Add Task
              </Button>
            </Group>
          </Stack>
        </Box>
      ) : (
        <Button
          variant="subtle"
          size="xs"
          leftSection={<Plus size={14} />}
          mt="xs"
          onClick={() => {
            if (onCreateTaskInline) {
              setShowAddTaskForm(true);
            } else {
              // Fallback to modal if inline creation not supported
              onAddTemplate(isVirtualGroup ? null : subGroup.id);
            }
          }}
        >
          Add task
        </Button>
      )}
    </Box>
  );
}

export default SubGroupSection;
